import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  CmpNewsletterRecipientSourceKindEnum,
  useCreateCmpNewsletterRecipientGroupMutation,
  useGetAllCategoriesQuery,
  useGetCmpManageMembersQuery,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { SectionShell } from "../../common/dashboard/SectionShell";
import { splitDirectRecipients } from "./emailBackendMappers";
import {
  EmailAudienceMemberOption,
  EmailFormState,
  EmailType,
  RecipientGroup,
  RecipientSourceId,
  nonMemberSubscribersRecipientSourceLabel,
} from "./emailMockData";
import {
  AudienceFilterId,
  AudienceFilters,
  FilterDropdown,
  SectionHeading,
  UpdateEmailField,
  checkboxClassName,
  dangerButtonClassName,
  formatNumber,
  inputClassName,
  memberSinceOptions,
  memberSizeOptions,
  primaryButtonClassName,
  renewalStatusOptions,
  secondaryButtonClassName,
  textareaClassName,
} from "./emailWizardShared";

export type StepOneAudienceProps = {
  email: EmailFormState;
  recipientGroups: RecipientGroup[];
  memberTypeOptions: Array<{ id: string; label: string }>;
  audienceFilters: AudienceFilters;
  intendedRecipientCount: number | null;
  isAudiencePreviewLoading: boolean;
  audiencePreviewError: string;
  isBusy: boolean;
  isSentRecord: boolean;
  toggleRecipientSource: (sourceId: RecipientSourceId) => void;
  toggleRecipientGroup: (groupId: string) => void;
  archiveRecipientGroup: (group: RecipientGroup) => Promise<void>;
  updateAudienceFilter: (filterId: AudienceFilterId, values: string[]) => void;
  clearAllFilters: () => void;
  updateEmailField: UpdateEmailField;
};

// Continuously managed audiences are first-class backend sources. Custom lists
// remain separate because their explicit member IDs and emails are snapshots.
const audienceCheckboxes: Array<{ id: RecipientSourceId; label: string }> = [
  { id: "allMembers", label: "Full Email Subscribers Lists" },
  { id: "activeMembers", label: "Active Members" },
  {
    id: "nonMemberSubscribers",
    label: nonMemberSubscribersRecipientSourceLabel,
  },
  { id: "newMembers", label: "New Members (within 60 days)" },
  {
    id: "renewingMembers",
    label: "Renewing Members (within 60 days)",
  },
  { id: "prospectMembers", label: "Prospect Members" },
];

// The eight communication types exactly as the Step-1 mock orders them
// (2 rows x 4 columns). The EmailType union covers all eight; the backend
// CmpNewsletterTypeEnum still lacks advocacyUpdate / memberWelcome /
// fromTheDesk / customEmail, so those persist as NEWSLETTER for now (see
// emailTypeToApi).
const communicationTypeCards: Array<[EmailType, string, string]> = [
  ["newsletter", "Newsletter", "Regular updates, news and highlights."],
  ["eBlast", "E-Blast", "Short, engaging announcements."],
  [
    "advocacyUpdate",
    "Advocacy Update",
    "Policy updates, surveys and action alerts.",
  ],
  [
    "pressRelease",
    "Media Release",
    "Official announcements for media distribution.",
  ],
  [
    "announcement",
    "Event Announcement",
    "Promote upcoming events and registrations.",
  ],
  ["memberWelcome", "Member Welcome", "Welcome new members to the chamber."],
  ["fromTheDesk", "From the Desk", "Message from the CEO or Executive."],
  ["customEmail", "Custom Email", "Create a custom email from scratch."],
];

type PopupCategoryOption = { id: string; label: string };

// Popup category buckets exactly as transcribed from the Create Custom
// Audience mock. They intentionally DIFFER from the Step-1 filter buckets
// (for example MEMBERSHIP SIZE "1-5 Employees" vs MEMBER SIZE "2-9
// employees") - do not unify.
const popupMemberStatusOptions: PopupCategoryOption[] = [
  { id: "status:ACTIVE", label: "Active Members" },
  { id: "status:PROSPECT", label: "Prospect Members" },
  { id: "status:PENDING", label: "Pending Members" },
  { id: "status:SUSPENDED", label: "Suspended Members" },
  { id: "status:DELETED", label: "Deleted Members" },
  { id: "status:new30", label: "New Members (within 30 days)" },
  { id: "status:new60", label: "New Members (within 60 days)" },
];
const popupMembershipSizeOptions: PopupCategoryOption[] = [
  { id: "size:1-5", label: "1-5 Employees" },
  { id: "size:6-20", label: "6-20 Employees" },
  { id: "size:21-50", label: "21-50 Employees" },
  { id: "size:51-100", label: "51-100 Employees" },
  { id: "size:101-250", label: "101-250 Employees" },
  { id: "size:250+", label: "250+ Employees" },
];
const popupMemberSinceOptions: PopupCategoryOption[] = [
  { id: "since:<1", label: "Less than 1 Year" },
  { id: "since:1-3", label: "1-3 Years" },
  { id: "since:3-5", label: "3-5 Years" },
  { id: "since:5-10", label: "5-10 Years" },
  { id: "since:10+", label: "10+ Years" },
];
const popupRenewalStatusOptions: PopupCategoryOption[] = [
  { id: "renewal:within30", label: "Renewing Within 30 Days" },
  { id: "renewal:within60", label: "Renewing Within 60 Days" },
  { id: "renewal:expired", label: "Expired Memberships" },
  { id: "renewal:recent", label: "Recently Renewed" },
];

const dayMilli = 24 * 60 * 60 * 1000;
const yearMilli = 365.25 * dayMilli;

const getEmployeeCount = (member: EmailAudienceMemberOption) =>
  (member.fullTimeEmployees || 0) + (member.partTimeEmployees || 0);

// A member belongs to a popup category when it matches that category's
// bucket. The popup audience is the UNION of every checked category (each
// checkbox adds that group of members), plus any typed-in email addresses.
const memberMatchesPopupCategory = (
  member: EmailAudienceMemberOption,
  categoryOptionId: string
): boolean => {
  const separatorIndex = categoryOptionId.indexOf(":");
  const group = categoryOptionId.slice(0, separatorIndex);
  const value = categoryOptionId.slice(separatorIndex + 1);

  if (group === "status") {
    if (value === "new30" || value === "new60") {
      const windowMilli = value === "new30" ? 30 * dayMilli : 60 * dayMilli;
      return (
        !!member.dateJoinedUtcMilli &&
        member.dateJoinedUtcMilli >= Date.now() - windowMilli
      );
    }
    return member.lifecycleStatus === value;
  }
  if (group === "type") {
    return member.membershipTypeId === value;
  }
  if (group === "size") {
    const employeeCount = getEmployeeCount(member);
    if (value === "1-5") return employeeCount >= 1 && employeeCount <= 5;
    if (value === "6-20") return employeeCount >= 6 && employeeCount <= 20;
    if (value === "21-50") return employeeCount >= 21 && employeeCount <= 50;
    if (value === "51-100") return employeeCount >= 51 && employeeCount <= 100;
    if (value === "101-250")
      return employeeCount >= 101 && employeeCount <= 250;
    return employeeCount > 250;
  }
  if (group === "industry") {
    return (member.categoryId || []).includes(value);
  }
  if (group === "since") {
    if (!member.dateJoinedUtcMilli) return false;
    const years = (Date.now() - member.dateJoinedUtcMilli) / yearMilli;
    if (value === "<1") return years < 1;
    if (value === "1-3") return years >= 1 && years < 3;
    if (value === "3-5") return years >= 3 && years < 5;
    if (value === "5-10") return years >= 5 && years < 10;
    return years >= 10;
  }
  // renewal buckets
  if (!member.renewalDateUtcMilli) return false;
  const daysUntilRenewal = Math.ceil(
    (member.renewalDateUtcMilli - Date.now()) / dayMilli
  );
  if (value === "within30") return daysUntilRenewal >= 0 && daysUntilRenewal <= 30;
  if (value === "within60") return daysUntilRenewal >= 0 && daysUntilRenewal <= 60;
  if (value === "expired") return daysUntilRenewal < 0;
  // "Recently Renewed": with annual renewals, a member renewed in the last
  // ~2 months still has 300+ days until the next renewal date.
  return daysUntilRenewal > 300;
};

const isPlausibleEmail = (address: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

const CategoryGroupList = ({
  title,
  options,
  selectedCategoryIds,
  onToggle,
}: {
  title: string;
  options: PopupCategoryOption[];
  selectedCategoryIds: string[];
  onToggle: (categoryOptionId: string) => void;
}) => (
  <div className="mb-6">
    <p className="mb-2 text-[13px] font-extrabold uppercase tracking-wide text-gray-500">
      {title}
    </p>
    {options.map((option) => (
      <label
        key={option.id}
        className="flex cursor-pointer items-center gap-3 py-1.5 text-[15px] text-black"
      >
        <input
          type="checkbox"
          className={checkboxClassName}
          checked={selectedCategoryIds.includes(option.id)}
          onChange={() => onToggle(option.id)}
        />
        {option.label}
      </label>
    ))}
  </div>
);

type CreateCustomAudiencePayload = {
  name: string;
  memberIds: string[];
  directEmails: string[];
};

const CreateCustomAudienceModal = ({
  memberTypeOptions,
  industryOptions,
  memberOptions,
  onClose,
  onCreate,
}: {
  memberTypeOptions: Array<{ id: string; label: string }>;
  industryOptions: Array<{ id: string; label: string }>;
  memberOptions: EmailAudienceMemberOption[];
  onClose: () => void;
  onCreate: (payload: CreateCustomAudiencePayload) => Promise<void>;
}) => {
  const [audienceName, setAudienceName] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [newEmailsText, setNewEmailsText] = useState("");
  const [nameError, setNameError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const membershipTypeCategoryOptions = useMemo(
    () =>
      memberTypeOptions.map((option) => ({
        id: `type:${option.id}`,
        label: option.label,
      })),
    [memberTypeOptions]
  );
  const industryCategoryOptions = useMemo(
    () =>
      industryOptions.map((option) => ({
        id: `industry:${option.id}`,
        label: option.label,
      })),
    [industryOptions]
  );
  const allCategoryOptionIds = useMemo(
    () =>
      [
        ...popupMemberStatusOptions,
        ...membershipTypeCategoryOptions,
        ...popupMembershipSizeOptions,
        ...industryCategoryOptions,
        ...popupMemberSinceOptions,
        ...popupRenewalStatusOptions,
      ].map((option) => option.id),
    [industryCategoryOptions, membershipTypeCategoryOptions]
  );
  const allCategoriesSelected =
    allCategoryOptionIds.length > 0 &&
    selectedCategoryIds.length === allCategoryOptionIds.length;

  const toggleCategory = (categoryOptionId: string) => {
    setSelectedCategoryIds((currentIds) =>
      currentIds.includes(categoryOptionId)
        ? currentIds.filter((id) => id !== categoryOptionId)
        : [...currentIds, categoryOptionId]
    );
  };

  const toggleSelectAllCategories = () => {
    setSelectedCategoryIds(allCategoriesSelected ? [] : allCategoryOptionIds);
  };

  const handleCreateAudience = async () => {
    const trimmedName = audienceName.trim();
    setNameError(trimmedName ? "" : "Enter a name for this audience.");
    setFormError("");
    if (!trimmedName) return;

    const directEmails = splitDirectRecipients(newEmailsText);
    const invalidEmails = directEmails.filter(
      (address) => !isPlausibleEmail(address)
    );
    if (invalidEmails.length > 0) {
      setFormError(`Check these email addresses: ${invalidEmails.join(", ")}`);
      return;
    }
    if (selectedCategoryIds.length === 0 && directEmails.length === 0) {
      setFormError(
        "Select at least one audience category or add an email address."
      );
      return;
    }

    const memberIds = memberOptions
      .filter((member) =>
        selectedCategoryIds.some((categoryOptionId) =>
          memberMatchesPopupCategory(member, categoryOptionId)
        )
      )
      .map((member) => member.id);

    setIsSaving(true);
    try {
      await onCreate({ name: trimmedName, memberIds, directEmails });
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "The custom audience could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 px-4 py-8">
      <div className="mx-auto w-full max-w-4xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-8 py-6">
          <div>
            <h2 className="text-[26px] font-extrabold leading-tight text-gray-950">
              Create Custom Audience
            </h2>
            <p className="mt-1 text-[15px] leading-6 text-gray-500">
              Build a custom audience by selecting member categories and adding
              new emails.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="mt-1 text-gray-400 transition hover:text-gray-600"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="space-y-8 px-8 py-7">
          <div>
            <SectionHeading index={1} title="Name Your Audience" />
            <input
              className={inputClassName}
              placeholder="e.g., Q3 Newsletter Recipients"
              value={audienceName}
              onChange={(event) => setAudienceName(event.target.value)}
            />
            {nameError ? (
              <p className="mt-2 text-[14px] font-extrabold text-red-600">
                {nameError}
              </p>
            ) : null}
          </div>

          <div>
            <SectionHeading
              index={2}
              title="Select Audience Categories"
              description="Search and select the member groups or filters you want to include."
            />
            <div className="rounded-md border border-gray-200 p-5">
              <label className="flex cursor-pointer items-center gap-3 text-[14px] font-extrabold uppercase tracking-wide text-gray-800">
                <input
                  type="checkbox"
                  className={checkboxClassName}
                  checked={allCategoriesSelected}
                  onChange={toggleSelectAllCategories}
                />
                Select All
              </label>
              <div className="mt-5 max-h-[360px] overflow-y-auto pr-2">
                <div className="grid grid-cols-1 gap-x-10 md:grid-cols-3">
                  <div>
                    <CategoryGroupList
                      title="Member Status"
                      options={popupMemberStatusOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                    <CategoryGroupList
                      title="Membership Types"
                      options={membershipTypeCategoryOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                  </div>
                  <div>
                    <CategoryGroupList
                      title="Membership Size"
                      options={popupMembershipSizeOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                    <CategoryGroupList
                      title="Industry"
                      options={industryCategoryOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                  </div>
                  <div>
                    <CategoryGroupList
                      title="Member Since"
                      options={popupMemberSinceOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                    <CategoryGroupList
                      title="Renewal Status"
                      options={popupRenewalStatusOptions}
                      selectedCategoryIds={selectedCategoryIds}
                      onToggle={toggleCategory}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <SectionHeading
              index={3}
              title={
                <>
                  Add New Emails <span className="text-gray-400">(Optional)</span>
                </>
              }
              description="Add individual email addresses that are not part of your existing lists."
            />
            <textarea
              className={textareaClassName}
              placeholder="Enter email addresses separated by commas or new lines"
              value={newEmailsText}
              onChange={(event) => setNewEmailsText(event.target.value)}
            />
            <p className="mt-2 text-[13px] leading-5 text-gray-500">
              Example: john@example.com, sarah@example.com
            </p>
          </div>

          {formError ? (
            <p className="text-[14px] font-extrabold text-red-600">
              {formError}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-8 py-5">
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={primaryButtonClassName}
            onClick={handleCreateAudience}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress color="inherit" size={18} /> : null}
            Create Audience
          </button>
        </div>
      </div>
    </div>
  );
};

const StepOneAudience = ({
  email,
  recipientGroups,
  memberTypeOptions,
  audienceFilters,
  intendedRecipientCount,
  isAudiencePreviewLoading,
  audiencePreviewError,
  isBusy,
  isSentRecord,
  toggleRecipientSource,
  toggleRecipientGroup,
  updateAudienceFilter,
  clearAllFilters,
  updateEmailField,
}: StepOneAudienceProps) => {
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: membersData } = useGetCmpManageMembersQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
  });
  const [createCmpNewsletterRecipientGroup] =
    useCreateCmpNewsletterRecipientGroupMutation();
  const [isAudiencePopupOpen, setIsAudiencePopupOpen] = useState(false);
  const [showAllFiltersNote, setShowAllFiltersNote] = useState(false);

  const industryOptions = useMemo(
    () =>
      [...(categoriesData?.getAllCategories || [])]
        .sort((firstCategory, secondCategory) =>
          firstCategory.name.localeCompare(secondCategory.name)
        )
        .map((category) => ({ id: String(category.id), label: category.name })),
    [categoriesData?.getAllCategories]
  );

  const popupMemberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows]
  );

  const customAudienceOptions = recipientGroups.map((group) => ({
    id: group.id,
    label: group.name,
  }));

  const handleCustomAudienceChange = (nextGroupIds: string[]) => {
    recipientGroups.forEach((group) => {
      const isSelected = email.recipientGroupIds.includes(group.id);
      const shouldBeSelected = nextGroupIds.includes(group.id);
      if (isSelected !== shouldBeSelected) toggleRecipientGroup(group.id);
    });
  };

  const createCustomAudience = async ({
    name,
    memberIds,
    directEmails,
  }: CreateCustomAudiencePayload) => {
    if (!chamberId) {
      throw new Error("Chamber information is still loading. Try again in a moment.");
    }

    // Explicit whitelist input build - never spread Apollo results here.
    const sourceKinds = [
      ...(memberIds.length > 0
        ? [CmpNewsletterRecipientSourceKindEnum.SelectedMembers]
        : []),
      ...(directEmails.length > 0
        ? [CmpNewsletterRecipientSourceKindEnum.DirectEmails]
        : []),
    ];
    const result = await createCmpNewsletterRecipientGroup({
      variables: {
        input: {
          chamberId,
          data: { name, memberIds, directEmails, sourceKinds },
        },
      },
      refetchQueries: ["GetCmpNewsletterRecipientGroups"],
      awaitRefetchQueries: true,
    });
    const savedGroup = result.data?.createCmpNewsletterRecipientGroup.group;
    if (!savedGroup) {
      throw new Error("The custom audience could not be saved.");
    }
    // Save then select: the new audience becomes a checked option in the
    // SELECT CUSTOM AUDIENCE LISTS dropdown right away.
    if (!email.recipientGroupIds.includes(savedGroup.id)) {
      toggleRecipientGroup(savedGroup.id);
    }
    setIsAudiencePopupOpen(false);
  };

  return (
    <div className="space-y-8">
      {isAudiencePopupOpen ? (
        <CreateCustomAudienceModal
          memberTypeOptions={memberTypeOptions}
          industryOptions={industryOptions}
          memberOptions={popupMemberOptions}
          onClose={() => setIsAudiencePopupOpen(false)}
          onCreate={createCustomAudience}
        />
      ) : null}

      {/* B10 (POL-002 Emails leg): shared SectionShell card with the
          transcription-bound numbered gold badge (variant="numbered"). */}
      <SectionShell
        id="email-step1-select-audience"
        variant="numbered"
        number={1}
        title="Select Your Audience"
        description="Choose who should receive this communication using member groups and audience filters."
      >
        <button
          type="button"
          className={`${primaryButtonClassName} mb-7`}
          onClick={() => setIsAudiencePopupOpen(true)}
          disabled={isBusy || isSentRecord}
        >
          Create Custom Audience
        </button>
        <div className="grid grid-cols-1 gap-x-12 gap-y-7 lg:grid-cols-3">
          {audienceCheckboxes.map((source) => (
            <label
              key={source.id}
              className="flex items-center gap-3 self-center text-[18px] text-gray-700"
            >
              <input
                type="checkbox"
                className={checkboxClassName}
                checked={email.recipientSourceIds.includes(source.id)}
                onChange={() => toggleRecipientSource(source.id)}
                disabled={isBusy || isSentRecord}
              />
              {source.label}
            </label>
          ))}
          <div className={isSentRecord ? "pointer-events-none opacity-60" : ""}>
            <FilterDropdown
              label="Select Custom Audience Lists"
              placeholder="Select"
              options={customAudienceOptions}
              values={email.recipientGroupIds}
              onChange={handleCustomAudienceChange}
            />
          </div>
        </div>
      </SectionShell>

      <SectionShell
        id="email-step1-refine-audience"
        variant="numbered"
        number={2}
        title={
          <>
            Refine Your Audience with Filters{" "}
            <span className="text-gray-400">(Optional)</span>
          </>
        }
        description="Use filters to narrow your audience and make your message more relevant."
        headerActions={
          <button
            type="button"
            className={dangerButtonClassName}
            onClick={clearAllFilters}
            disabled={isBusy || isSentRecord}
          >
            Clear All Filters
          </button>
        }
      >
        <div className={isSentRecord ? "pointer-events-none opacity-60" : ""}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <FilterDropdown
              label="Member Types"
              placeholder="Filter by membership type"
              options={memberTypeOptions}
              values={audienceFilters.membershipTypeIds}
              onChange={(values) =>
                updateAudienceFilter("membershipTypeIds", values)
              }
            />
            <FilterDropdown
              label="Member Size"
              placeholder="Filter by number of employees"
              options={memberSizeOptions}
              values={audienceFilters.employeeRangeIds}
              onChange={(values) =>
                updateAudienceFilter("employeeRangeIds", values)
              }
            />
            <FilterDropdown
              label="Industry"
              placeholder="Filter by category"
              options={industryOptions}
              values={audienceFilters.categoryIds}
              onChange={(values) => updateAudienceFilter("categoryIds", values)}
            />
            <FilterDropdown
              label="Member Since"
              placeholder="Filter by number of years"
              options={memberSinceOptions}
              values={audienceFilters.memberSinceRangeIds}
              onChange={(values) =>
                updateAudienceFilter("memberSinceRangeIds", values)
              }
            />
            <FilterDropdown
              label="Renewal Status"
              placeholder="Filter by up and coming renewal"
              options={renewalStatusOptions}
              values={audienceFilters.renewalRangeIds}
              onChange={(values) =>
                updateAudienceFilter("renewalRangeIds", values)
              }
            />
          </div>
        </div>
        <div className="mt-7">
          <button
            type="button"
            className={primaryButtonClassName}
            onClick={() => setShowAllFiltersNote(true)}
          >
            <AddIcon />
            Add Another Filter
          </button>
          {showAllFiltersNote ? (
            <p className="mt-2 text-[14px] text-gray-600">
              All available filters are already shown above.
            </p>
          ) : null}
        </div>
        <div className="mt-7 flex items-center gap-3 rounded-md border border-[#EFDCAC] bg-[#FDF8EB] px-5 py-4">
          <BoltIcon className="shrink-0 text-cpaGold" />
          <p className="text-[15px] font-extrabold leading-6 text-black">
            Tip: Filters help you send the right message to the right people.
            This improves engagement and results.
          </p>
        </div>
      </SectionShell>

      <SectionShell
        id="email-step1-estimated-audience"
        variant="numbered"
        number={3}
        title="Estimated Audience"
        description="Review your audience size before choosing your communication type."
      >
        <div className="inline-block min-w-[220px] rounded-xl border border-gray-200 bg-white px-6 py-5">
          <p className="text-[34px] font-extrabold leading-none text-black">
            {intendedRecipientCount === null
              ? "—"
              : formatNumber(intendedRecipientCount)}
          </p>
          <p
            className={`mt-3 flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-wide ${
              audiencePreviewError
                ? "text-red-600"
                : isAudiencePreviewLoading
                  ? "text-gray-500"
                  : "text-emerald-600"
            }`}
            aria-live="polite"
          >
            {isAudiencePreviewLoading ? (
              <CircularProgress color="inherit" size={14} />
            ) : null}
            {audiencePreviewError
              ? "Estimate unavailable"
              : isAudiencePreviewLoading
                ? "Calculating recipients"
                : "Estimated Recipients"}
          </p>
          {audiencePreviewError ? (
            <p className="mt-2 max-w-sm text-[13px] leading-5 text-red-600">
              {audiencePreviewError}
            </p>
          ) : null}
        </div>
      </SectionShell>

      <SectionShell
        id="email-step1-communication-type"
        variant="numbered"
        number={4}
        title="Choose the Type of Communication"
        description="Select the type of email or communication you want to create."
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {communicationTypeCards.map(([value, label, description]) => (
            <button
              key={value}
              type="button"
              className={`relative min-h-[138px] rounded-xl border p-5 text-left transition ${
                email.emailType === value
                  ? "border-cpaGold bg-[#FCF5E8]"
                  : "border-gray-200 bg-white hover:border-cpaGold"
              }`}
              onClick={() => updateEmailField("emailType", value)}
              disabled={isBusy || isSentRecord}
            >
              {email.emailType === value ? (
                <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-cpaGold text-white">
                  <CheckIcon fontSize="small" />
                </span>
              ) : null}
              <span className="block pr-8 text-[20px] font-extrabold text-black">
                {label}
              </span>
              <span className="mt-2 block text-[15px] leading-6 text-black">
                {description}
              </span>
            </button>
          ))}
        </div>
      </SectionShell>
    </div>
  );
};

export default StepOneAudience;
